// DetectionSettings.jsx — onglet « Détection IA » de la page Gestion.
// Même panneau « Paramètres avancés » que le module AI Detection (seuil de confiance, second
// modèle dommages, validation VLM), appliqué ici à tous les dossiers à partir du suivant.
import React, { useEffect, useState } from 'react';
import { FiSliders, FiInfo, FiCpu, FiClock, FiAlertTriangle, FiCheckCircle, FiX, FiRotateCcw } from 'react-icons/fi';
import './DetectionSettings.css';

function sliderColor(value) {
  if (value < 33) return '#ef4444';
  if (value < 66) return '#f97316';
  return '#22c55e';
}

function sliderLabel(value) {
  if (value < 33) return 'Faible';
  if (value < 66) return 'Modéré';
  return 'Élevé';
}

const toForm = (data) => ({
  seuil: Math.round(data.confidence * 100),
  ensemble: data.ensemble,
  vlm: data.vlm,
  zoom: data.zoom,
});

const DetectionSettings = ({ baseUrl, token }) => {
  const [saved, setSaved] = useState(null);
  const [form, setForm] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const applyResponse = (data) => {
    setSaved(data);
    setForm(toForm(data));
  };

  useEffect(() => {
    const load = async () => {
      try {
        const res = await fetch(`${baseUrl}/detection-settings/`, {
          headers: { 'Authorization': `Bearer ${token}` },
        });
        if (!res.ok) throw new Error('Erreur lors du chargement des paramètres de détection');
        applyResponse(await res.json());
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [baseUrl, token]);

  if (loading) {
    return (
      <div className="loading-spinner">
        <div className="spinner"></div>
        <p>Chargement des paramètres...</p>
      </div>
    );
  }
  if (!form) return error ? <div className="error-toast"><FiAlertTriangle aria-hidden="true" /><span>{error}</span></div> : null;

  const current = toForm(saved);
  const changed = form.seuil !== current.seuil || form.ensemble !== current.ensemble || form.vlm !== current.vlm || form.zoom !== current.zoom;
  const color = sliderColor(form.seuil);

  const save = async () => {
    if (form.vlm && !current.vlm) {
      const ok = window.confirm(
        "Activer la validation VLM pour tous les dossiers ?\n\nCompter environ une minute par photo."
      );
      if (!ok) return;
    }
    setSaving(true);
    setError('');
    try {
      const res = await fetch(`${baseUrl}/detection-settings/`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({ confidence: form.seuil / 100, ensemble: form.ensemble, vlm: form.vlm, zoom: form.zoom }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.detail || "Erreur lors de l'enregistrement des paramètres");
      applyResponse(data);
      setSuccess('Paramètres enregistrés : appliqués dès le prochain dossier');
      setTimeout(() => setSuccess(''), 4000);
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const reset = () => setForm({
    seuil: Math.round(saved.defaults.confidence * 100),
    ensemble: saved.defaults.ensemble,
    vlm: saved.defaults.vlm,
    zoom: saved.defaults.zoom,
  });

  const revert = () => setForm(toForm(saved));

  const Toggle = ({ id, checked, onChange }) => (
    <span className="ds-switch">
      <input id={id} type="checkbox" checked={checked} onChange={onChange} />
      <span className="ds-switch-track" aria-hidden="true" />
    </span>
  );

  return (
    <div className="detection-settings">
      <div className="toast-stack" aria-live="polite">
        {error && (
          <div className="toast error-toast" role="alert">
            <FiAlertTriangle aria-hidden="true" />
            <span>{error}</span>
            <button type="button" className="toast-close" onClick={() => setError('')} aria-label="Fermer">
              <FiX />
            </button>
          </div>
        )}
        {success && (
          <div className="toast success-toast" role="status">
            <FiCheckCircle aria-hidden="true" />
            <span>{success}</span>
            <button type="button" className="toast-close" onClick={() => setSuccess('')} aria-label="Fermer">
              <FiX />
            </button>
          </div>
        )}
      </div>

      <section className="ds-card">
        <div className="ds-card-header">
          <span className="ds-card-icon"><FiSliders /></span>
          <div>
            <h2 className="ds-card-title">Seuil de confiance</h2>
            <p className="ds-card-sub">Appliqué à tous les dossiers à partir du prochain.</p>
          </div>
          <div className="ds-slider-value">
            <span className="ds-badge" style={{ background: `${color}22`, color }}>{sliderLabel(form.seuil)}</span>
            <span className="ds-percent">{form.seuil}%</span>
          </div>
        </div>
        <div className="ds-card-body">
          <div className="ds-slider-label">
            <label htmlFor="ds-seuil">Seuil de confiance avancé</label>
            <span
              className="ds-info"
              tabIndex={0}
              title="Seuil de confiance du modèle de détection des dommages : plus il est bas, plus de dommages sont détectés. Corrompu, Casse et déboîtement restent à 30 % au minimum."
            >
              <FiInfo />
            </span>
          </div>
          <input
            id="ds-seuil"
            type="range"
            min={0}
            max={100}
            step={1}
            value={form.seuil}
            onChange={e => setForm(prev => ({ ...prev, seuil: Number(e.target.value) }))}
            className="ds-range"
            style={{
              background: `linear-gradient(to right, ${color} 0%, ${color} ${form.seuil}%, #e5e7eb ${form.seuil}%, #e5e7eb 100%)`,
              accentColor: color,
              '--ds-thumb': color,
            }}
          />
          <div className="ds-range-legend"><span>0% — Inclure tout</span><span>100% — Certitude totale</span></div>
          <p className="ds-hint">
            Plus le seuil est bas, plus de dommages sont détectés (et plus de faux positifs).
            Corrompu, Casse et déboîtement restent à 30 % au minimum.
          </p>
        </div>
      </section>

      <section className="ds-card">
        <div className="ds-card-header">
          <span className="ds-card-icon"><FiCpu /></span>
          <div>
            <h2 className="ds-card-title">Modèles et validation</h2>
            <p className="ds-card-sub">Options complémentaires de la détection.</p>
          </div>
        </div>
        <div className="ds-card-body ds-options">
          <label className={`ds-option${form.ensemble ? ' on' : ''}`} htmlFor="ds-ensemble">
            <span className="ds-option-text">
              <span className="ds-check-title">Second modèle dommages</span>
              <span className="ds-check-help">
                L'ancien modèle (640 px) ajoute les dommages que le modèle principal n'a pas vus, à partir de
                25 % de confiance. Les dommages du modèle principal sont gardés au seuil choisi ci-dessus
                (Corrompu, Casse et déboîtement : 30 % au minimum).
              </span>
            </span>
            <Toggle
              id="ds-ensemble"
              checked={form.ensemble}
              onChange={e => setForm(prev => ({ ...prev, ensemble: e.target.checked }))}
            />
          </label>

          <label className={`ds-option${form.vlm ? ' on' : ''}`} htmlFor="ds-vlm">
            <span className="ds-option-text">
              <span className="ds-check-title">
                Validation VLM des dommages
                <span className="ds-tag"><FiClock /> ~1 min / photo</span>
              </span>
              <span className="ds-check-help">
                Un modèle de vision revoit chaque dommage détecté (reflets, pièce voisine, faux positifs) et
                recherche les dommages manqués sur chaque pièce.
              </span>
              {form.vlm && !saved.vlm_configured && (
                <span className="ds-check-warning">
                  <FiAlertTriangle aria-hidden="true" />
                  Clé de la passerelle VLM absente du .env du serveur : les dossiers seront analysés sans VLM.
                </span>
              )}
            </span>
            <Toggle
              id="ds-vlm"
              checked={form.vlm}
              onChange={e => setForm(prev => ({ ...prev, vlm: e.target.checked }))}
            />
          </label>

          <label className={`ds-option${form.zoom ? ' on' : ''}`} htmlFor="ds-zoom">
            <span className="ds-option-text">
              <span className="ds-check-title">
                Gros plan par pièce
                <span className="ds-tag"><FiClock /> analyse plus longue</span>
              </span>
              <span className="ds-check-help">
                Relance le modèle dommages sur chaque pièce recadrée pour retrouver les petits dommages.
                Aucun gain mesuré sur 50 dossiers clos : à réserver aux essais.
              </span>
            </span>
            <Toggle
              id="ds-zoom"
              checked={form.zoom}
              onChange={e => setForm(prev => ({ ...prev, zoom: e.target.checked }))}
            />
          </label>
        </div>
      </section>

      <div className={`ds-savebar${changed ? ' dirty' : ''}`}>
        <div className="ds-savebar-info">
          {changed ? (
            <span className="ds-dirty"><span className="ds-dot" /> Modifications non enregistrées</span>
          ) : saved.updated_by ? (
            <span className="ds-meta">
              Dernière modification par {saved.updated_by}
              {saved.updated_at && ` le ${new Date(saved.updated_at).toLocaleString('fr-FR')}`}
            </span>
          ) : (
            <span className="ds-meta">Aucune modification</span>
          )}
        </div>
        <div className="ds-footer-right">
          <button type="button" className="ds-reset" onClick={reset}>
            <FiRotateCcw /> Valeurs par défaut
          </button>
          {changed && (
            <button type="button" className="cancel-btn" onClick={revert} disabled={saving}>
              Annuler
            </button>
          )}
          <button type="button" className="submit-btn" disabled={!changed || saving} onClick={save}>
            {saving ? 'Enregistrement...' : 'Enregistrer'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default DetectionSettings;
